import { useState, useEffect } from 'react';

/**
 * Custom hook for Leaflet map readiness
 *
 * Unlike Google Maps, Leaflet doesn't require API loading.
 * This hook provides a consistent interface for the components.
 *
 * @returns {Object} { isLoaded: boolean, loadError: Error | null }
 *
 * @example
 * function MyComponent() {
 *   const { isLoaded, loadError } = useLeafletMap();
 *
 *   if (loadError) return <div>Error loading maps</div>;
 *   if (!isLoaded) return <div>Loading maps...</div>;
 *
 *   return <MapContainer>...</MapContainer>;
 * }
 */
export const useLeafletMap = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError] = useState(null);

  useEffect(() => {
    // Leaflet is loaded via npm package, so it's always available
    // We just need a minimal delay to ensure CSS is loaded
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return { isLoaded, loadError };
};
